/**
 * Component Property Resolver
 *
 * Resolves component property assignments to target nodes in expanded instances.
 * Maps: defID → componentPropDef → componentPropRefs → target nodes
 */

import type { GuidRef } from '../types/base.js'
import type { ComponentPropDef } from '../types/properties/symbol.js'
import type {
	ComponentPropAssignment,
	ComponentPropRef,
} from '../types/properties/instance.js'
import type { SceneNode } from '../types/scene.js'
import type { TextProps } from '../types/properties/text.js'

/**
 * Property definition lookup key
 */
function guidRefToKey(ref: GuidRef): string {
	return `${ref.sessionID}:${ref.localID}`
}

/**
 * Build a lookup map from defID to ComponentPropDef
 *
 * @param symbolNode - The SYMBOL node containing componentPropDefs
 * @returns Map of defID (as string) to ComponentPropDef
 */
export function buildPropDefLookup(
	symbolNode: SceneNode,
): Map<string, ComponentPropDef> {
	const map = new Map<string, ComponentPropDef>()
	const props = symbolNode.properties as any

	const propDefs = props.componentPropDefs as ComponentPropDef[] | undefined

	if (!propDefs) return map

	for (const propDef of propDefs) {
		const key = guidRefToKey(propDef.id)
		map.set(key, propDef)
	}

	return map
}

/**
 * Find all nodes with componentPropRefs matching a defID
 *
 * @param root - Root node to search from
 * @param defID - The property definition ID to match
 * @returns Array of {node, ref} tuples
 */
export function findNodesWithPropRef(
	root: SceneNode,
	defID: string,
): Array<{ node: SceneNode; ref: ComponentPropRef }> {
	const results: Array<{ node: SceneNode; ref: ComponentPropRef }> = []

	function traverse(node: SceneNode) {
		const props = node.properties as any
		const propRefs = props.componentPropRefs as ComponentPropRef[] | undefined

		if (propRefs) {
			for (const ref of propRefs) {
				const refKey = guidRefToKey(ref.defID)
				if (refKey === defID) {
					results.push({ node, ref })
				}
			}
		}

		// Traverse children
		if (node.children) {
			for (const child of node.children) {
				traverse(child)
			}
		}
	}

	traverse(root)
	return results
}

/**
 * Apply component property assignments to an expanded node tree
 *
 * @param expandedNode - The expanded symbol node tree
 * @param assignments - Property assignments from the instance
 * @param propDefLookup - Lookup map of property definitions
 */
export function applyComponentPropAssignments(
	expandedNode: SceneNode,
	assignments: ComponentPropAssignment[],
	propDefLookup: Map<string, ComponentPropDef>,
): void {
	for (const assignment of assignments) {
		const defKey = guidRefToKey(assignment.defID)
		const propDef = propDefLookup.get(defKey)

		if (!propDef) {
			console.warn(
				`Component property definition not found for defID: ${defKey}`,
			)
			continue
		}

		// Find target nodes
		// Note: We don't check propDef.type here because it may not be present in the data.
		// Instead, we rely on the componentPropNodeField in the propRef to determine the type.
		const targets = findNodesWithPropRef(expandedNode, defKey)

		if (targets.length === 0) {
			console.warn(
				`No target nodes found for property: ${propDef.name} (${defKey})`,
			)
			continue
		}

		// Apply the override to each target
		for (const { node, ref } of targets) {
			applyPropertyOverride(node, ref, assignment, propDef)
		}
	}
}

/**
 * Apply a single property override to a target node
 *
 * @internal Exported for testing purposes
 */
export function applyPropertyOverride(
	node: SceneNode,
	propRef: ComponentPropRef,
	assignment: ComponentPropAssignment,
	_propDef: ComponentPropDef,
): void {
	const props = node.properties as any
	const field = propRef.componentPropNodeField

	// Handle TEXT_DATA override
	if (field === 'TEXT_DATA' && assignment.value.textValue) {
		if (node.type === 'TEXT') {
			const textProps = props as TextProps
			textProps.textData = {
				characters: assignment.value.textValue.characters,
				lines: assignment.value.textValue.lines,
			}
		} else {
			console.warn(`TEXT_DATA override on non-TEXT node: ${node.type}`)
		}
	}

	// Handle VISIBLE override (BOOLEAN)
	else if (field === 'VISIBLE' && assignment.value.boolValue !== undefined) {
		props.visible = assignment.value.boolValue
	}

	/**
	 * Handle OVERRIDDEN_SYMBOL_ID override (INSTANCE_SWAP)
	 *
	 * This field allows component instances to swap which child component they use.
	 * Example: A Button component with an Icon property can swap between different
	 * icon symbols (Checkmark, Arrow, Plus, etc.).
	 *
	 * Implementation:
	 * 1. Update the symbolData.symbolID to point to the new symbol (guidValue)
	 * 2. The transformer's recursive expansion will expand the instance with the new symbolID
	 * 3. This happens BEFORE nested instance expansion, ensuring correct flow
	 *
	 * Note: Only applies to INSTANCE nodes. Other node types log a warning.
	 */
	else if (field === 'OVERRIDDEN_SYMBOL_ID') {
		// Validate guidValue exists
		if (!assignment.value.guidValue) {
			console.warn(
				`OVERRIDDEN_SYMBOL_ID assignment missing guidValue for node: ${node.guid}`,
			)
			return
		}

		// Only applies to INSTANCE nodes
		if (node.type === 'INSTANCE') {
			const instanceProps = props as any

			// Update the symbolID to point to the new component
			// This happens BEFORE nested instance expansion, so the expansion
			// process will use the updated symbolID
			instanceProps.symbolData.symbolID = {
				sessionID: assignment.value.guidValue.sessionID,
				localID: assignment.value.guidValue.localID,
			}
		} else {
			console.warn(`OVERRIDDEN_SYMBOL_ID override on non-INSTANCE node: ${node.type}`)
		}
	}

	// Handle other fields
	else {
		console.warn(`Unsupported component property field: ${field}`)
	}
}
